import pickBy from 'lodash/pickBy'
import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoShipDeckCompartment,
  GeckoShipDeckCompartmentJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { GeckoEditableComponent } from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoFixedSensor,
  GeckoFixedSensorJSON,
} from 'types/assetModel/features/geckoFixedSensor'
import {
  GeckoNozzle,
  GeckoNozzleJSON,
} from 'types/assetModel/features/geckoNozzle'
import {
  GeckoPolygonalPlate,
  GeckoPolygonalPlateJSON,
} from 'types/assetModel/features/geckoPolygonalPlate'
import {
  GeckoShipDeckBitt,
  GeckoShipDeckBittJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckBitt'
import {
  GeckoShipDeckBulkhead,
  GeckoShipDeckBulkheadJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckBulkhead'
import {
  GeckoShipDeckChock,
  GeckoShipDeckChockJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckChock'
import {
  GeckoShipDeckCoaming,
  GeckoShipDeckCoamingJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckCoaming'
import {
  GeckoShipDeckDoor,
  GeckoShipDeckDoorJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckDoor'
import {
  GeckoShipDeckDrain,
  GeckoShipDeckDrainJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckDrain'
import {
  GeckoShipDeckHatch,
  GeckoShipDeckHatchJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckHatch'
import {
  GeckoShipDeckLadder,
  GeckoShipDeckLadderJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckLadder'
import {
  GeckoShipDeckLocker,
  GeckoShipDeckLockerJSON,
} from 'types/assetModel/features/shipDeck/geckoShipDeckLocker'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { requiredNumberSchema, requiredStringSchema } from 'utils/zodHelpers'

type ShipDeckSupportedFeatures =
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON
  | GeckoNozzleJSON
  | GeckoFixedSensorJSON
  | GeckoShipDeckCompartmentJSON
  | GeckoShipDeckBittJSON
  | GeckoShipDeckCoamingJSON
  | GeckoShipDeckChockJSON
  | GeckoShipDeckHatchJSON
  | GeckoShipDeckLockerJSON
  | GeckoShipDeckLadderJSON
  | GeckoShipDeckBulkheadJSON
  | GeckoShipDeckDoorJSON
  | GeckoShipDeckDrainJSON
  | GeckoPolygonalPlateJSON

export type ShipDeckAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.ShipDeck
  length: number
  start_width: number
  end_width: number
  features: Record<string, ShipDeckSupportedFeatures>
  scene_config: Record<string, unknown>
  component_bounds: Record<string, number>
}

export const ShipDeckAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.ShipDeckCompartment,
  GeckoFeatureModelType.ShipDeckChock,
  GeckoFeatureModelType.ShipDeckBitt,
  GeckoFeatureModelType.ShipDeckCoaming,
  GeckoFeatureModelType.ShipDeckHatch,
  GeckoFeatureModelType.PolygonalPlate,
  GeckoFeatureModelType.ShipDeckLocker,
  GeckoFeatureModelType.ShipDeckDrain,
  GeckoFeatureModelType.ShipDeckLadder,
  GeckoFeatureModelType.ShipDeckBulkhead,
  GeckoFeatureModelType.ShipDeckDoor,
]

export const NewShipDeckAssetModelJSON = {
  name: 'New Ship Deck',
  type: GeckoComponentModelType.ShipDeck,
  length: 0,
  start_width: 0,
  end_width: 0,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
} as ShipDeckAssetModelJSON

export class GeckoShipDeck extends GeckoEditableComponent {
  type: GeckoComponentModelType.ShipDeck
  length: GeckoMeasurement
  startWidth: GeckoMeasurement
  endWidth: GeckoMeasurement
  componentBounds: GeckoComponentRect
  sceneConfig = undefined
  polygonalPlates: Map<string, GeckoPolygonalPlate>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  verticalWelds: Map<string, GeckoVerticalWeld>
  nozzles: Map<string, GeckoNozzle>
  fixedSensors: Map<string, GeckoFixedSensor>
  compartments: Map<string, GeckoShipDeckCompartment>
  chocks: Map<string, GeckoShipDeckChock>
  bitts: Map<string, GeckoShipDeckBitt>
  coamings: Map<string, GeckoShipDeckCoaming>
  hatches: Map<string, GeckoShipDeckHatch>
  lockers: Map<string, GeckoShipDeckLocker>
  bulkheads: Map<string, GeckoShipDeckBulkhead>
  doors: Map<string, GeckoShipDeckDoor>
  drains: Map<string, GeckoShipDeckDrain>
  ladders: Map<string, GeckoShipDeckLadder>
  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    length: GeckoMeasurement,
    startWidth: GeckoMeasurement,
    endWidth: GeckoMeasurement,
    componentBounds: GeckoComponentRect,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    nozzles: Map<string, GeckoNozzle>,
    fixedSensors: Map<string, GeckoFixedSensor>,
    compartments: Map<string, GeckoShipDeckCompartment>,
    chocks: Map<string, GeckoShipDeckChock>,
    bitts: Map<string, GeckoShipDeckBitt>,
    coamings: Map<string, GeckoShipDeckCoaming>,
    hatches: Map<string, GeckoShipDeckHatch>,
    lockers: Map<string, GeckoShipDeckLocker>,
    bulkheads: Map<string, GeckoShipDeckBulkhead>,
    doors: Map<string, GeckoShipDeckDoor>,
    drains: Map<string, GeckoShipDeckDrain>,
    ladders: Map<string, GeckoShipDeckLadder>,
    polygonalPlates: Map<string, GeckoPolygonalPlate>
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.ShipDeck
    this.length = length
    this.startWidth = startWidth
    this.endWidth = endWidth
    this.componentBounds = componentBounds
    this.polygonalPlates = polygonalPlates
    this.horizontalWelds = horizontalWelds
    this.verticalWelds = verticalWelds
    this.nozzles = nozzles
    this.fixedSensors = fixedSensors
    this.compartments = compartments
    this.chocks = chocks
    this.bitts = bitts
    this.coamings = coamings
    this.hatches = hatches
    this.lockers = lockers
    this.bulkheads = bulkheads
    this.doors = doors
    this.drains = drains
    this.ladders = ladders
  }

  public get maxWidth() {
    return this.startWidth.num >= this.endWidth.num
      ? this.startWidth
      : this.endWidth
  }

  public get minWidth() {
    return this.startWidth.num < this.endWidth.num
      ? this.startWidth
      : this.endWidth
  }

  public get polygonalPlatesArray() {
    return Array.from(this.polygonalPlates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public get nozzlesArray() {
    return Array.from(this.nozzles.values())
  }

  public get fixedSensorsArray() {
    return Array.from(this.fixedSensors.values())
  }

  public get compartmentsArray() {
    return Array.from(this.compartments.values())
  }

  public get chocksArray() {
    return Array.from(this.chocks.values())
  }

  public get bittsArray() {
    return Array.from(this.bitts.values())
  }

  public get coamingsArray() {
    return Array.from(this.coamings.values())
  }

  public get hatchesArray() {
    return Array.from(this.hatches.values())
  }

  public get lockersArray() {
    return Array.from(this.lockers.values())
  }

  public get laddersArray() {
    return Array.from(this.ladders.values())
  }

  public get bulkheadsArray() {
    return Array.from(this.bulkheads.values())
  }

  public get doorsArray() {
    return Array.from(this.doors.values())
  }

  public get drainsArray() {
    return Array.from(this.drains.values())
  }

  public static CreateFromJSON(id: string, json: ShipDeckAssetModelJSON) {
    const name = json.name as string

    const length = GeckoMeasurement.CreateFromValue(
      json.length as number,
      GeckoMeasurementUnit.Meters
    )
    const startWidth = GeckoMeasurement.CreateFromValue(
      json.start_width as number,
      GeckoMeasurementUnit.Meters
    )
    const endWidth = GeckoMeasurement.CreateFromValue(
      json.end_width as number,
      GeckoMeasurementUnit.Meters
    )

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const featureData = json.features as Record<
      string,
      ShipDeckSupportedFeatures
    >

    const childHorizontalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.HorizontalWeld
    })
    const horizontalWelds = GeckoHorizontalWeld.CreateMapFromJSON(
      id,
      childHorizontalWeldJSON as Record<string, GeckoHorizontalWeldJSON>
    )

    const childVerticalWeldJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.VerticalWeld
    })
    const verticalWelds = GeckoVerticalWeld.CreateMapFromJSON(
      id,
      childVerticalWeldJSON as Record<string, GeckoVerticalWeldJSON>
    )

    const childNozzleJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.Nozzle
    })
    const nozzles = GeckoNozzle.CreateMapFromJSON(
      id,
      childNozzleJSON as Record<string, GeckoNozzleJSON>
    )

    const childFixedSensorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.FixedSensor
    })
    const fixedSensors = GeckoFixedSensor.CreateMapFromJSON(
      id,
      childFixedSensorJSON as Record<string, GeckoFixedSensorJSON>
    )

    const childChockJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckChock
    })

    const chocks = GeckoShipDeckChock.CreateMapFromJSON(
      id,
      childChockJSON as Record<string, GeckoShipDeckChockJSON>
    )

    const childCompartmentJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckCompartment
    })
    const compartments = GeckoShipDeckCompartment.CreateMapFromJSON(
      id,
      childCompartmentJSON as Record<string, GeckoShipDeckCompartmentJSON>
    )

    const childBittJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckBitt
    })

    const bitts = GeckoShipDeckBitt.CreateMapFromJSON(
      id,
      childBittJSON as Record<string, GeckoShipDeckBittJSON>
    )

    const childCoamingJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckCoaming
    })

    const coamings = GeckoShipDeckCoaming.CreateMapFromJSON(
      id,
      childCoamingJSON as Record<string, GeckoShipDeckCoamingJSON>
    )

    const childHatchJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckHatch
    })

    const hatches = GeckoShipDeckHatch.CreateMapFromJSON(
      id,
      childHatchJSON as Record<string, GeckoShipDeckHatchJSON>
    )

    const childLockerJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckLocker
    })

    const lockers = GeckoShipDeckLocker.CreateMapFromJSON(
      id,
      childLockerJSON as Record<string, GeckoShipDeckLockerJSON>
    )

    const childBulkheadJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckBulkhead
    })

    const bulkheads = GeckoShipDeckBulkhead.CreateMapFromJSON(
      id,
      childBulkheadJSON as Record<string, GeckoShipDeckBulkheadJSON>
    )

    const childDoorJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckDoor
    })

    const doors = GeckoShipDeckDoor.CreateMapFromJSON(
      id,
      childDoorJSON as Record<string, GeckoShipDeckDoorJSON>
    )

    const childDrainJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckDrain
    })

    const drains = GeckoShipDeckDrain.CreateMapFromJSON(
      id,
      childDrainJSON as Record<string, GeckoShipDeckDrainJSON>
    )

    const childLadderJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.ShipDeckLadder
    })

    const ladders = GeckoShipDeckLadder.CreateMapFromJSON(
      id,
      childLadderJSON as Record<string, GeckoShipDeckLadderJSON>
    )

    const childPolygonalPlateJSON = pickBy(featureData, (child) => {
      return child.type === GeckoFeatureModelType.PolygonalPlate
    })
    const polygonalPlates = GeckoPolygonalPlate.CreateMapFromJSON(
      id,
      childPolygonalPlateJSON as Record<string, GeckoPolygonalPlateJSON>
    )

    return new GeckoShipDeck(
      id,
      json,
      name,
      length,
      startWidth,
      endWidth,
      componentBounds,
      horizontalWelds,
      verticalWelds,
      nozzles,
      fixedSensors,
      compartments,
      chocks,
      bitts,
      coamings,
      hatches,
      lockers,
      bulkheads,
      doors,
      drains,
      ladders,
      polygonalPlates
    )
  }

  public asJSON(): ShipDeckAssetModelJSON {
    const baseJSON = generateShipDeck(
      this.name,
      this.length,
      this.componentBounds.y.start,
      this.startWidth,
      this.endWidth,
      this.json as ShipDeckAssetModelJSON
    )

    return baseJSON
  }
}

const generateShipDeck = (
  name: string,
  length: GeckoMeasurement,
  yStart: GeckoMeasurement,
  startWidth: GeckoMeasurement,
  endWidth: GeckoMeasurement,
  otherShipDeckData: ShipDeckAssetModelJSON = NewShipDeckAssetModelJSON
): ShipDeckAssetModelJSON => {
  const deckYStart = yStart
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num
  const deckLength = length
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const deckStartWidth = startWidth
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num
  const deckEndWidth = endWidth
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const maxWidth = Math.max(deckStartWidth, deckEndWidth)
  const halfMaxWidth = Number((maxWidth / 2).toPrecision(assetModelPrecision))

  return {
    name: name,
    type: GeckoComponentModelType.ShipDeck,
    length: deckLength,
    start_width: deckStartWidth,
    end_width: deckEndWidth,
    features: otherShipDeckData.features,
    scene_config: otherShipDeckData.scene_config,
    component_bounds: {
      x_start: -halfMaxWidth,
      y_start: deckYStart,
      x_end: halfMaxWidth,
      y_end: deckYStart + deckLength,
    },
  }
}

export const ShipDeckAssetModelFormFields = (
  shipDeckJSON: ShipDeckAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Ship Deck Name',
      value: shipDeckJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y_start',
      label: 'Y Start',
      value: shipDeckJSON.component_bounds.y_start,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      initialValue: 0,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'length',
      label: 'Length',
      value: shipDeckJSON.length,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'start_width',
      label: 'Start Width',
      value: shipDeckJSON.start_width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'end_width',
      label: 'End Width',
      value: shipDeckJSON.end_width,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const ShipDeckJSONFromFormFields = (
  component: ShipDeckAssetModelJSON,
  formFields: FormField[]
) => {
  return generateShipDeck(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'length')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y_start')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'start_width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'end_width')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    component
  )
}
